import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import requiredIf from 'react-required-if';
import {
  headingColor,
  serifFont,
  sansSerifFont,
  h1FontFamily,
  h1FontSize,
  h1FontWeight,
  h1Tracking,
  h1LineHeight,
  h1IsUpperCase,
  h2FontFamily,
  h2FontSize,
  h2FontWeight,
  h2Tracking,
  h2LineHeight,
  h2IsUpperCase,
  h3FontFamily,
  h3FontSize,
  h3FontWeight,
  h3Tracking,
  h3LineHeight,
  h3IsUpperCase,
  h4FontFamily,
  h4FontSize,
  h4FontWeight,
  h4Tracking,
  h4LineHeight,
  h4IsUpperCase,
  h5FontFamily,
  h5FontSize,
  h5FontWeight,
  h5Tracking,
  h5LineHeight,
  h5IsUpperCase,
  h6FontFamily,
  h6FontSize,
  h6FontWeight,
  h6Tracking,
  h6LineHeight,
  h6IsUpperCase,
  PropsWithTheme
} from '@nib-components/theme';

import {color as getColor, colorValues, map, validColorValues} from '@nib/layout';

export const validSizeValues = [1, 2, 3, 4, 5, 6] as const;
export const validComponentValues = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'label', 'span', 'header', 'p'] as const;
const validBreakpointValues = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'] as const;
const validFontTypeValues = ['serif', 'sans-serif'] as const;
export const validAlignValues = ['left', 'center', 'right'] as const;

type sizeValues = (typeof validSizeValues)[number];
type componentValues = (typeof validComponentValues)[number];
type breakpointValues = (typeof validBreakpointValues)[number];
type fontTypeValues = (typeof validFontTypeValues)[number];
type alignValues = (typeof validAlignValues)[number];

type PartialRecord<K extends keyof never, T> = {
  [P in K]?: T;
};

type ResponsiveProp<T> = PartialRecord<breakpointValues, T>;
type ResponsiveOrStaticProp<T> = T | ResponsiveProp<T>;

type NamedColor = colorValues;
type OtherColor = ((props: PropsWithTheme) => string) | string;

export interface HeadingProps {
  size: ResponsiveOrStaticProp<sizeValues>;
  component?: componentValues | React.Component | (() => string);
  color?: NamedColor | OtherColor;
  fontType?: fontTypeValues;
  align?: ResponsiveOrStaticProp<alignValues>;
  children?: React.ReactNode;
  [key: string]: unknown; // other props
}

export const h1 = () => css`
  font-family: ${h1FontFamily};
  font-size: ${h1FontSize};
  font-weight: ${h1FontWeight};
  letter-spacing: ${h1Tracking};
  line-height: ${h1LineHeight};
  ${props => h1IsUpperCase(props) && 'text-transform: uppercase;'};
`;

export const h2 = () => css`
  font-family: ${h2FontFamily};
  font-size: ${h2FontSize};
  font-weight: ${h2FontWeight};
  letter-spacing: ${h2Tracking};
  line-height: ${h2LineHeight};
  ${props => h2IsUpperCase(props) && 'text-transform: uppercase;'};
`;

const h3 = () => css`
  font-family: ${h3FontFamily};
  font-size: ${h3FontSize};
  font-weight: ${h3FontWeight};
  letter-spacing: ${h3Tracking};
  line-height: ${h3LineHeight};
  ${props => h3IsUpperCase(props) && 'text-transform: uppercase;'};
`;

const h4 = () => css`
  font-family: ${h4FontFamily};
  font-size: ${h4FontSize};
  font-weight: ${h4FontWeight};
  letter-spacing: ${h4Tracking};
  line-height: ${h4LineHeight};
  ${props => h4IsUpperCase(props) && 'text-transform: uppercase;'};
`;

const h5 = () => css`
  font-family: ${h5FontFamily};
  font-size: ${h5FontSize};
  font-weight: ${h5FontWeight};
  letter-spacing: ${h5Tracking};
  line-height: ${h5LineHeight};
  ${props => h5IsUpperCase(props) && 'text-transform: uppercase;'};
`;

const h6 = () => css`
  font-family: ${h6FontFamily};
  font-size: ${h6FontSize};
  font-weight: ${h6FontWeight};
  letter-spacing: ${h6Tracking};
  line-height: ${h6LineHeight};
  ${props => h6IsUpperCase(props) && 'text-transform: uppercase;'};
`;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const BaseHeadingComponent = (props: HeadingProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {align, size, component, fontType, color, children, ...otherProps} = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let Component: any = component;

  // if size is a number, we can infer the component
  if (!Component && typeof size === 'number') {
    Component = `h${size}` as componentValues;
  }

  //eslint-disable-next-line no-undef
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const invariant = require('invariant');
    invariant(Component || typeof size === 'number', '@nib-components/heading: Must have component prop set if size prop is an object.');
  }

  return <Component {...otherProps}>{children}</Component>;
};

BaseHeadingComponent.propTypes = {
  size: PropTypes.oneOfType([
    PropTypes.oneOf(validSizeValues),
    PropTypes.shape({
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
      xxl: PropTypes.number,
      xxxl: PropTypes.number
    })
  ]).isRequired,
  align: PropTypes.oneOfType([
    PropTypes.oneOf(validAlignValues),
    PropTypes.shape({
      xs: PropTypes.oneOf(validAlignValues),
      sm: PropTypes.oneOf(validAlignValues),
      md: PropTypes.oneOf(validAlignValues),
      lg: PropTypes.oneOf(validAlignValues),
      xl: PropTypes.oneOf(validAlignValues),
      xxl: PropTypes.oneOf(validAlignValues),
      xxxl: PropTypes.oneOf(validAlignValues)
    })
  ]),
  component: requiredIf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]), (props: {size: ResponsiveOrStaticProp<sizeValues>}) => typeof props.size === 'object'),
  children: PropTypes.node,
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
};

const isNamedColor = (color?: OtherColor) => {
  if (!color) return false;
  if (validColorValues.includes(color as colorValues)) {
    return true;
  }
  return false;
};

const setColor: any = (props: HeadingProps) => {
  if (isNamedColor(props.color)) {
    return getColor(props);
  }
  // Else return the old color behavior (selector or raw string)
  return css`
    color: ${props.color || headingColor};
  `;
};

const Heading = styled(BaseHeadingComponent)<HTMLHeadingElement>`
  margin: 0;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  ${props => setColor({...props, color: props.color!})};

  ${({size}) =>
    size &&
    map(size, (val: sizeValues) => {
      if (val === 1) return h1();
      if (val === 2) return h2();
      if (val === 3) return h3();
      if (val === 4) return h4();
      if (val === 5) return h5();
      if (val === 6) return h6();
    })};

  ${({align}) =>
    map(align, (val: alignValues) => {
      if (align) {
        return css`
          text-align: ${val};
        `;
      }

      return '';
    })}

  ${({theme, fontType}) =>
    fontType
      ? theme.id === 'nib'
        ? fontType === 'sans-serif'
          ? css`
              &&& {
                font-family: ${sansSerifFont};
                font-weight: 700;
              }
            `
          : css`
              &&& {
                font-family: ${serifFont};
                font-weight: 600;
              }
            `
        : ``
      : ``};
`;

Heading.propTypes = {
  size: PropTypes.oneOfType([
    PropTypes.oneOf(validSizeValues),
    PropTypes.shape({
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
      xxl: PropTypes.number,
      xxxl: PropTypes.number
    })
  ]).isRequired,
  component: requiredIf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]), (props: {size: ResponsiveOrStaticProp<sizeValues>}) => typeof props.size === 'object'),
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  fontType: PropTypes.oneOf(validFontTypeValues),
  children: PropTypes.node
};

Heading.displayName = 'Heading';

export default Heading;
